// src/ui/format.js — display helpers
import { tsMs } from '../services/chats';

/** 🟢 A just-sent message has `timestamp: serverTimestamp()`, which reads as NULL
 *  locally until the server resolves it — that's why the bubble appeared with NO
 *  time and then "adjusted". Fall back to now so it shows the correct clock time
 *  immediately (the server value replaces it moments later, identical to the eye). */
export function timeLabel(t, late = false) {
  const ms = tsMs(t) || Date.now();
  const s = new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  /* 🟢 `late` comes from showsLateSendTime (services/messageOrder). Messages are
     ordered by when this client SAW them, which can seat a slow message below a
     reply that was sent after it — so the clock reads backwards down the screen
     unless that one bubble says the time is when it was SENT. The time itself is
     unchanged; only the word in front of it is new. */
  return late ? `sent ${s}` : s;
}

export function dayLabel(t) {
  const ms = tsMs(t);
  if (!ms) return '';
  const d = new Date(ms); const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = (startOf(now) - startOf(d)) / 86400000;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Today at 3:45 PM" / "Yesterday at 9:12 PM" / "12 Aug 2026 at 3:45 PM".
 *  Used by the media viewer's centre block, where a bare clock time is ambiguous
 *  (you can open a photo from months ago). */
export function fullTimeLabel(t) {
  const ms = tsMs(t) || (typeof t === 'number' ? t : 0);
  if (!ms) return '';
  const d = new Date(ms);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const day = dayLabel(ms);
  return day === 'Today' || day === 'Yesterday' ? `${day} at ${time}` : `${day} at ${time}`;
}

export function initials(name = '') {
  return (name.trim()[0] || '?').toUpperCase();
}

/** "last seen today at 3:45 PM" — same rules as the mobile ChatHeaderStatus:
 *  absolute clock time (never goes stale like "1h ago"), and CALENDAR-DAY
 *  comparison so "yesterday" is right even a few hours across midnight. */
export function lastSeenLabel(ms) {
  if (!ms) return '';
  const d = new Date(typeof ms === 'number' ? ms : Number(ms));
  if (!d.getTime()) return '';
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startThat = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startToday - startThat) / 86400000);

  if (dayDiff <= 0) return `last seen today at ${time}`;
  if (dayDiff === 1) return `last seen yesterday at ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  const date = d.toLocaleDateString([], sameYear
    ? { month: 'short', day: 'numeric' }
    : { year: 'numeric', month: 'short', day: 'numeric' });
  return `last seen ${date} at ${time}`;
}

/** Nothing to say — and it has to be an OBJECT, because the caller ranks it. */
const EMPTY_PREVIEW = { text: '', ts: 0, seq: null, senderId: null, type: null, thumbs: [], extra: 0, source: null };

/* 🟥 THE ROW SAID "📷 Photo" WHEN IT WAS HOLDING THE PHOTO. Every media message
   carries a tiny base64 still — the blur the chat itself paints behind the real
   image while it loads — so the list had a thumbnail available all along and
   drew a glyph instead. It costs nothing: no decryption, no fetch, no network.
   Sealed media is no obstacle either, because the still is not the ciphertext. */
export function previewTiles(m) {
  if (!m) return { thumbs: [], extra: 0 };
  if (m.type === 'image' && m.imageThumbnailBase64) {
    return { thumbs: [{ b64: m.imageThumbnailBase64 }], extra: 0 };
  }
  if (m.type === 'video' && m.videoThumbnailBase64) {
    return { thumbs: [{ b64: m.videoThumbnailBase64, video: true }], extra: 0 };
  }
  if (m.type === 'images') {
    const tiles = Array.isArray(m.batchThumbnails) ? m.batchThumbnails : [];
    const n = Array.isArray(m.images) ? m.images.length : tiles.length;
    const shown = tiles.slice(0, 4);
    if (!shown.some(Boolean)) return { thumbs: [], extra: 0 };
    /* 🟢 FOUR, THEN "+N" — the same shape the live path already draws, so an
       album looks identical whether the row is quoting a document or the
       phone's export. `kinds` is not available here (it needs the sealed key
       map the export does not carry), so a tile is not labelled as video; the
       count and the pictures are right, which is what the row is for. */
    return { thumbs: shown.map((b64) => ({ b64 })), extra: Math.max(0, n - 4) };
  }
  return { thumbs: [], extra: 0 };
}

const TYPE_PREVIEW = {
  image: '📷 Photo', images: '📷 Album', video: '🎥 Video', audio: '🎤 Voice message',
  document: '📄 Document', sticker: 'Sticker', gif: 'GIF', location: '📍 Location',
  contact: '👤 Contact', poll: '📊 Poll', security: '🔐 New login detected',
};

/** Chat-row preview.
 *  The Firestore `lastMessage` is an E2EE PLACEHOLDER ("🔒 Encrypted message") — the
 *  real text only exists decrypted. So prefer the last entry of the phone-synced
 *  history for this chat, and fall back to a type label; never show the lock string. */
/**
 * The row's fallback text: the phone's exported history first, then whatever the
 * chat document remembers.
 *
 * @param opts.hidden        ids this browser has hidden locally (hiddenMessages.js)
 * @param opts.myUid         to honour `deletedFor` inside the export
 * @param opts.ignoreChatDoc true when the newest message in Firestore is one I
 *                           deleted — see below
 *
 * 🟥 THE EXPORT DOES NOT KNOW ABOUT DELETIONS, AND IT NEVER WILL. It is a
 *    snapshot the phone built and shipped; "delete for me" is a decision this
 *    browser made afterwards, recorded either on the Firestore document or in
 *    localStorage. So a message deleted here is still sitting in the export, and
 *    a fallback that reads the export blindly hands it straight back to the row —
 *    which is exactly what happened: delete the last message and the chat list
 *    answers with its plaintext.
 */
/**
 * The same answer, WITH THE TIME IT BELONGS TO.
 *
 * 🟥 THE ROW USED TO CHOOSE ITS TEXT BY SOURCE. `preview?.text || fallbackText`
 *    — a surviving Firestore document beat the phone's export unconditionally,
 *    with the two times never compared. That reads as a sensible order of
 *    preference (live before snapshot) and is wrong, because the two are not
 *    describing the same moment: store-and-forward deletes documents UNEVENLY,
 *    on the receiving phone, on chat open, and never for the sender's own
 *    messages — so the newest SURVIVING document is routinely older than the
 *    newest message the export knows about, and the row pinned itself to it.
 *
 *    A day of texting from the phone would land in the export and be rendered
 *    nowhere, while the list went on quoting the last thing this browser had
 *    sent, which is exactly the class of document that outlives everything else.
 *
 * 🟢 SO THE CALLER NEEDS THE TIME, NOT JUST THE WORDS. `ts` is what makes the
 *    two comparable; `senderId` comes with it because the unread veto reads it
 *    and would otherwise be answered about a different message.
 */
export function chatPreviewFrom(chat, syncedForChat, opts = {}) {
  const { hidden = null, myUid = null, ignoreChatDoc = false } = opts;
  const visible = (m) => {
    if (!m) return false;
    if (hidden && m.id && hidden.has(m.id)) return false;
    if (myUid && Array.isArray(m.deletedFor) && m.deletedFor.includes(myUid)) return false;
    return true;
  };

  // 🟥 NEWEST BY TIMESTAMP, NOT BY POSITION. This took `[length - 1]`, assuming the
  //    export was oldest-first — it is the exact opposite: WebHistorySync sorts
  //    descending before capping, so the last element is the OLDEST message it shipped.
  //    The row was quoting a message from months ago and calling it the latest.
  let last = null;
  if (Array.isArray(syncedForChat) && syncedForChat.length) {
    let bestTs = -Infinity;
    for (const m of syncedForChat) {
      if (!visible(m)) continue;
      /* 🟥 THE ARCHIVE HOLDS TWO SHAPES OF TIME. The phone's export writes
         `timestamp` in milliseconds; a row RETAINED from the live stream
         (historySync.retainLive) keeps the Firestore stamp it arrived with,
         which survives IndexedDB as a plain `{seconds, nanoseconds}`.
         `Number()` of that is NaN, so every retained row scored 0 and lost to
         any exported one however old — the newest photo in the chat was
         out-ranked by a text from before it. tsMs reads both. */
      const t = tsMs(m?.timestamp);
      if (t >= bestTs) { bestTs = t; last = m; }
    }
  }
  if (last) {
    /* 🟢 THE EXPORT'S OWN STAMP, IN MILLISECONDS. WebHistorySync writes
       `timestamp: tsMs(...)` on every exported message, so this is already the
       same unit the live preview resolves to and the two can simply be
       compared. A message the export cannot describe (no plaintext, no type
       label, no caption) falls through to the chat document rather than
       claiming the row with an empty string. */
    /* 🟥 THE seq TRAVELS WITH THE PREVIEW. Two phones are seconds apart, so `ts`
       cannot decide which of two previews is newer — `newerPreview` needs the
       server's sequence number to compare them, and it can only have it if it
       is carried here. See services/lastMessage.js. */
    const at = { id: last.id || null, ts: tsMs(last.timestamp), seq: last.seq ?? null, senderId: last.senderId || null, type: last.type || null, ...previewTiles(last), source: 'export' };
    if (last.type === 'text' && last._plain) return { ...at, text: last._plain };
    if (TYPE_PREVIEW[last.type]) return { ...at, text: TYPE_PREVIEW[last.type] };
    if (last._caption) return { ...at, text: last._caption };
  }

  /* 🟥 THE CHAT DOCUMENT DESCRIBES THE MESSAGE I JUST REMOVED. `lastMessage` and
     `lastMessageType` are written by the SENDER and shared with the other person,
     so deleting a message for myself alone cannot — must not — clear them. When
     the caller has established that the newest message in Firestore is one I
     hid, these two fields are describing it, and quoting them would undo the
     delete in the only place the delete was supposed to show. */
  if (ignoreChatDoc) return EMPTY_PREVIEW;

  /* 🟢 THE CHAT DOCUMENT'S LINE IS, BY CONSTRUCTION, THE NEWEST MESSAGE. Every
     sender writes `lastMessage` in the same operation as the message itself, so
     the honest time to put on it is the document's own activity stamp — not 0,
     which would rank the newest thing in the conversation below everything. */
  const at = {
    /* 🟢 the id travels too, so the row can prove it is quoting the newest
       message rather than guessing from the clock — see messageMissing */
    id: chat?.lastMessageId || null,
    ts: Math.max(tsMs(chat?.timestamp), tsMs(chat?.lastMessageTime), tsMs(chat?.updatedAt)),
    /* 🟢 the phone writes lastMessageSeq alongside the preview now, so the chat
       document can be compared with the exported history on the same key. */
    seq: chat?.lastMessageSeq ?? null,
    senderId: chat?.lastMessageSenderId || null,
    type: chat?.lastMessageType || null,
    source: 'chatdoc',
  };
  if (TYPE_PREVIEW[chat?.lastMessageType]) return { ...at, text: TYPE_PREVIEW[chat.lastMessageType] };
  const lm = chat?.lastMessage || '';
  // hide any encrypted placeholder / raw ciphertext
  if (!lm || lm.includes('Encrypted') || lm.includes('🔒')) return EMPTY_PREVIEW;
  return { ...at, text: lm };
}

/** The words alone, for callers that do not have to rank anything. */
export function chatPreviewText(chat, syncedForChat, opts = {}) {
  return chatPreviewFrom(chat, syncedForChat, opts).text;
}
